<template>
  <LeadCombobox
    :items="items"
    :model-value="draftLabel"
    free-text-entry
    clearable
    :loading="store.isLoading"
    :placeholder="$t('candidate.fields.add_placeholder')"
    :disabled="disabled"
    @update:model-value="(v) => (draftLabel = typeof v === 'string' ? v : null)"
    @option:selected="onOptionSelected"
    @option:created="onOptionCreated" />
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from 'vue';
import LeadCombobox from '@/components/form/LeadCombobox.vue';
import { useCandidateFieldsStore } from '@/stores/candidateFields';
import type { CandidateFieldDefinition } from '@/types/candidate';

interface FieldOptionItem {
  id: string;
  name: string;
  definition: CandidateFieldDefinition;
  [key: string]: unknown;
}

interface Props {
  exclude?: string[];
  disabled?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  exclude: () => [],
  disabled: false,
});

const emit = defineEmits<{
  (e: 'select', definition: CandidateFieldDefinition): void;
  (e: 'create', label: string): void;
}>();

const store = useCandidateFieldsStore();
const draftLabel = ref<string | null>(null);

onMounted(() => store.fetchDefinitions());

// LeadCombobox's default getOptionLabel/getOptionValue read `name`/`id`, so definitions are
// mapped to that shape. Its freeTextEntry "create" row carries its own marker (see
// useFreeTextCreateOption), so a definition is never mistaken for one — option:created fires
// only for a value the user typed that matches no definition.
const items = computed(() =>
  store.sortedDefinitions
    .filter((definition) => !props.exclude.includes(definition.field_key))
    .map((definition) => ({ id: definition.field_key, name: definition.label, definition }))
);

const onOptionSelected = (option: any) => {
  emit('select', (option as FieldOptionItem).definition);
  draftLabel.value = null;
};

const onOptionCreated = (label: string) => {
  emit('create', label);
  draftLabel.value = null;
};
</script>
